import React, { useState } from "react";
import { env } from "../helpers/GlobalStateProvider";
import axios from "axios";
import { getApiUrl } from "../config";
import { getToken } from "../helpers/tokenManager";
import { DialogBody, DialogCloseTrigger, DialogContent, DialogFooter, DialogHeader, DialogRoot, DialogTitle } from "../components/ui/dialog";
import { Flex, Link, Stack, Text } from "@chakra-ui/react";
import { Button } from "../components/ui/button";
import { Checkbox } from "../components/ui/checkbox";
import { LuExternalLink } from "react-icons/lu";

interface TosAgreementModalProps {
  onClose: () => void;
}

const TosAgreementModal: React.FC<TosAgreementModalProps> = ({ onClose }) => {
  const [agreed, setAgreed] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState("");

  const handleAgree = async () => {
    if (!agreed) {
      setMessage("Please check the agreement box before continuing.");
      return;
    }

    setIsLoading(true);
    try {
      await axios.post(
        `${getApiUrl(env)}/v3/apiKey/agree_tos`,
        {},
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${getToken()}`,
          },
        }
      );

      // Close the modal on success
      onClose();
    } catch (error: any) {
      if (process.env.NODE_ENV === 'development') console.error("Error agreeing to TOS:", error);
      setMessage("Error recording agreement. Please try again later.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <DialogRoot open onOpenChange={(e) => { if (!e.open) onClose(); }}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Terms of Service Agreement</DialogTitle>
          <DialogCloseTrigger />
        </DialogHeader>
        <DialogBody>
          <Stack gap={5}>
            <Text fontSize="md" color="#52525b" lineHeight="24px">
              Please review our Terms of Service and Privacy Policy before
              continuing.
            </Text>

            <Flex gap={6} flexWrap="wrap">
              <Link
                href="https://coincap.io/terms-of-service?tab=terms"
                target="_blank"
                rel="noopener noreferrer"
                color="#9333ea"
                fontSize="sm"
                fontWeight="semibold"
              >
                View Terms of Service <LuExternalLink />
              </Link>
              <Link
                href="https://coincap.io/terms-of-service?tab=privacy"
                target="_blank"
                rel="noopener noreferrer"
                color="#9333ea"
                fontSize="sm"
                fontWeight="semibold"
              >
                View Privacy Policy <LuExternalLink />
              </Link>
            </Flex>

            <Checkbox
              checked={agreed}
              onCheckedChange={(e: any) => setAgreed(!!e.checked)}
            >
              <Text fontSize="sm" color="#18181b">
                I have read and agree to the Terms of Service and Privacy Policy
              </Text>
            </Checkbox>

            {message && (
              <Text fontSize="sm" color="#dc2626">
                {message}
              </Text>
            )}
          </Stack>
        </DialogBody>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={isLoading}>
            Cancel
          </Button>
          <Button
            colorPalette="purple"
            bg="#9333ea"
            onClick={handleAgree}
            disabled={!agreed}
            loading={isLoading}
          >
            I Agree
          </Button>
        </DialogFooter>
      </DialogContent>
    </DialogRoot>
  );
};

export default TosAgreementModal;
